<template>
  <div class="charts-manager p-4 border rounded">
    <div class="flex justify-between items-center mb-4">
      <h3>Charts Configuration</h3>
      <Button
        label="Add Chart"
        icon="pi pi-plus"
        @click="openNewChartDialog"
        :disabled="!hasDimensions"
      />
    </div>

    <!-- Warning if no dimensions -->
    <div v-if="!hasDimensions" class="p-4 bg-yellow-50 border border-yellow-200 rounded mb-4">
      <p class="text-yellow-700">
        <i class="pi pi-exclamation-triangle mr-2"></i>
        Please add dimensions before creating charts.
      </p>
    </div>

    <!-- Charts List -->
    <div v-if="charts && charts.length > 0" class="space-y-4">
      <div v-for="(chart, index) in charts" :key="chart._id || index" class="p-4 border rounded">
        <div class="flex justify-between items-start mb-4">
          <div>
            <h4>{{ chart.title?.en || 'Untitled Chart' }}</h4>
            <p class="text-sm text-gray-600 flex flex-wrap items-center gap-2">
              <span>{{ chart.type || 'bar' }} Chart</span>
              <Tag
                v-if="chartVisibilityLabel(chart)"
                :value="chartVisibilityLabel(chart)"
                severity="secondary"
                class="text-xs"
              />
            </p>
          </div>
          <div class="flex gap-2">
            <Button
              icon="pi pi-pencil"
              class="p-button-rounded p-button-text"
              @click="editChart(index)"
            />
            <Button
              icon="pi pi-trash"
              class="p-button-rounded p-button-text p-button-danger"
              @click="deleteChart(index)"
            />
          </div>
        </div>

        <!-- Chart Preview -->
        <div
          v-if="isValidChartData(chart)"
          class="chart-preview h-64 flex flex-col min-h-0"
        >
          <div class="flex-1 min-h-0">
            <BarChartComponent
              v-if="chart.type === 'boxplot'"
              :chart-config="getBoxplotChartConfig(chart)"
              :dimensions="dimensions"
              class="w-full h-full"
            />
            <Chart
              v-else
              :type="chart.type"
              :data="getChartData(chart)"
              :options="getChartOptions()"
              class="w-full h-full"
            />
          </div>
          <LikertScaleLegend
            v-if="boxplotPreviewUsesLikert(chart)"
            variant="belowChart"
          />
        </div>
        <div v-else class="chart-preview h-64 flex items-center justify-center text-gray-500">
          Invalid chart data
        </div>
      </div>
    </div>

    <!-- Chart Dialog -->
    <ChartDialog
      v-model:visible="chartDialogVisible"
      :is-editing="isEditing"
      :chart="currentChart"
      :assessment-id="currentAssessmentSchema._id"
      @save="saveChart"
      @cancel="closeChartDialog"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Chart from 'primevue/chart';
import ChartDialog from './ChartDialog.vue';
import BarChartComponent from '@/components/Home/DashboardComponent/BarChartComponent.vue';
import LikertScaleLegend from '@/components/Home/DashboardComponent/LikertScaleLegend.vue';

export default {
  name: 'ChartsManager',
  components: {
    Button,
    Tag,
    Chart,
    ChartDialog,
    BarChartComponent,
    LikertScaleLegend,
  },
  data() {
    return {
      chartDialogVisible: false,
      isEditing: false,
      currentChartIndex: -1,
      currentChart: {
        type: 'bar',
        title: {
          en: '',
          de: '',
        },
        description: {
          en: '',
          de: '',
        },
        data: {
          labels: [],
          datasets: [
            {
              label: 'Dataset 1',
              data: [],
              backgroundColor: 'rgba(255, 99, 132, 0.5)',
              borderColor: 'rgb(255, 99, 132)',
            },
          ],
        },
        dimensions: [],
        options: {},
        barBoxplotWhenAggregated: {
          enabled: false,
          ownResults: false,
          teamResults: true,
        },
        visibility: 'both',
      },
    };
  },
  computed: {
    ...mapState('assessmentSchemas', ['currentAssessmentSchema']),
    ...mapState('dimensions', ['dimensions']),
    charts() {
      if (!this.currentAssessmentSchema?._id) {
        return [];
      }
      const chart = this.$store.getters['charts/getChartByAssessmentId'](this.currentAssessmentSchema._id);
      return chart;
    },
    hasDimensions() {
      // Check if the current assessment schema has assigned dimensions
      return this.currentAssessmentSchema?.dimensions?.length > 0;
    },
  },
  watch: {
    // 'currentAssessmentSchema.dimensions': {
    //   async handler(newDimensions) {
    //     if (newDimensions?.length > 0 && this.currentAssessmentSchema?._id) {
    //       await this.$store.dispatch('charts/fetchLinkableAssessments', {
    //         assessmentId: this.currentAssessmentSchema._id,
    //         dimensions: newDimensions,
    //       });
    //     }
    //   },
    // },
  },
  mounted() {
    if (this.currentAssessmentSchema?._id) {
      this.$store.dispatch('charts/fetchChartByAssessmentId', this.currentAssessmentSchema._id);
      // Fetch dimensions for chart creation
      this.$store.dispatch('dimensions/fetchDimensions');
      // if (this.currentAssessmentSchema.dimensions?.length > 0) {
      //   this.$store.dispatch('charts/fetchLinkableAssessments', {
      //     assessmentId: this.currentAssessmentSchema._id,
      //     dimensions: this.currentAssessmentSchema.dimensions,
      //   });
      // }
    }
  },
  methods: {
    chartVisibilityLabel(chart) {
      const v = chart?.visibility || 'both';
      const map = {
        own: this.$t('ChartDialog.visibilityOwn'),
        team: this.$t('ChartDialog.visibilityTeam'),
        both: this.$t('ChartDialog.visibilityBoth'),
      };
      return map[v] || map.both;
    },
    boxplotPreviewUsesLikert(chart) {
      if (chart?.type !== 'boxplot' || !this.isValidChartData(chart)) return false;
      const cfg = this.getBoxplotChartConfig(chart);
      const y = cfg?.options?.scales?.y;
      if (!y || y.min !== 0) return false;
      const max = y.max ?? y.suggestedMax;
      return max === 5 || max === 6;
    },
    isValidChartData(chart) {
      if (!chart || !chart.data) return false;
      const { labels, datasets } = chart.data;
      if (!Array.isArray(labels) || !Array.isArray(datasets) || datasets.length === 0) {
        return false;
      }
      if (chart.type === 'boxplot') {
        return datasets.every((ds) => Array.isArray(ds.data));
      }
      return datasets.every((ds) => Array.isArray(ds.data) && ds.data.every((v) => typeof v === 'number'));
    },
    openNewChartDialog() {
      this.isEditing = false;
      this.currentChartIndex = -1;
      this.currentChart = {
        type: 'bar',
        title: {
          en: '',
          de: '',
        },
        description: {
          en: '',
          de: '',
        },
        data: {
          labels: [],
          datasets: [
            {
              label: 'Dataset 1',
              data: [],
              backgroundColor: 'rgba(255, 99, 132, 0.5)',
              borderColor: 'rgb(255, 99, 132)',
            },
          ],
        },
        dimensions: [],
        options: {},
        barBoxplotWhenAggregated: {
          enabled: false,
          ownResults: false,
          teamResults: true,
        },
        visibility: 'both',
      };
      this.chartDialogVisible = true;
    },
    editChart(index) {
      this.isEditing = true;
      this.currentChartIndex = index;
      this.currentChart = { ...this.$store.getters['charts/getChartByAssessmentId'](this.currentAssessmentSchema._id)[index] };
      this.chartDialogVisible = true;
    },
    async deleteChart(index) {
      const chartToDelete = this.$store.getters['charts/getChartByAssessmentId'](this.currentAssessmentSchema._id)[index];
      if (chartToDelete._id) {
        await this.$store.dispatch('charts/deleteChart', chartToDelete._id);
      }
    },
    closeChartDialog() {
      this.chartDialogVisible = false;
      this.isEditing = false;
      this.currentChartIndex = -1;
      this.currentChart = {
        type: 'bar',
        title: {
          en: '',
          de: '',
        },
        description: {
          en: '',
          de: '',
        },
        data: {
          labels: [],
          datasets: [
            {
              label: 'Dataset 1',
              data: [],
              backgroundColor: 'rgba(255, 99, 132, 0.5)',
              borderColor: 'rgb(255, 99, 132)',
            },
          ],
        },
        dimensions: [],
        options: {},
        barBoxplotWhenAggregated: {
          enabled: false,
          ownResults: false,
          teamResults: true,
        },
        visibility: 'both',
      };
    },
    async saveChart(chartData) {
      if (this.currentAssessmentSchema?._id) {
        try {
          await this.$store.dispatch('charts/saveChart', {
            ...chartData,
            assessmentId: this.currentAssessmentSchema._id,
          });
          // Refresh the charts list after saving
          await this.$store.dispatch('charts/fetchChartByAssessmentId', this.currentAssessmentSchema._id);
          this.closeChartDialog();
        } catch (error) {
          console.error('Error saving chart:', error);
        }
      }
    },
    getBoxplotChartConfig(chart) {
      if (!this.isValidChartData(chart) || chart.type !== 'boxplot') {
        return { type: 'boxplot', data: { labels: [], datasets: [] }, options: {} };
      }
      const labels = chart.data.labels || [];
      const ds = chart.data.datasets[0] || {};
      const placeholderBox = {
        min: 1,
        q1: 2,
        median: 3,
        q3: 4,
        max: 5,
        mean: 3,
        sd: 1,
      };
      const data = labels.map(() => ({ ...placeholderBox }));
      return {
        type: 'boxplot',
        data: {
          labels,
          datasets: [{
            label: ds.label || 'Distribution',
            data,
            backgroundColor: ds.backgroundColor || 'rgba(200, 58, 121, 0.7)',
            borderColor: ds.borderColor || '#000000',
            borderWidth: 1,
          }],
        },
        options: { scales: { y: { min: 0, max: 5 } } },
      };
    },
    getChartData(chart) {
      if (!this.isValidChartData(chart)) {
        return {
          labels: [],
          datasets: [{
            label: 'No Data',
            data: [],
            backgroundColor: 'rgba(200, 200, 200, 0.5)',
            borderColor: 'rgb(200, 200, 200)',
          }],
        };
      }

      const hexToRgba = (hex, alpha = 0.5) => {
        if (!hex) return 'rgba(255, 99, 132, 0.5)';
        const cleanHex = hex.replace('#', '');
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      const hexToRgb = (hex) => {
        if (!hex) return 'rgb(255, 99, 132)';
        const cleanHex = hex.replace('#', '');
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return `rgb(${r}, ${g}, ${b})`;
      };

      // Create a new data object with converted colors
      const chartData = {
        labels: chart.data.labels,
        datasets: chart.data.datasets.map((dataset) => ({
          ...dataset,
          backgroundColor: hexToRgba(dataset.backgroundColor),
          borderColor: hexToRgb(dataset.borderColor),
        })),
      };

      console.log('Chart Data with converted colors:', chartData); // Debug log
      return chartData;
    },
    getChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
        },
        scales: {
          y: {
            beginAtZero: true,
          },
        },
        elements: {
          line: {
            tension: 0.4,
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.charts-manager {
  background-color: white;
}

.chart-preview {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 1rem;
}
</style>
